// Earlier recordings of this visit (G4): the raw transcript of each, and its
// audio while the hospital's retention period lasts. Collapsed by default so
// it never pushes the recorder down.
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { listRecordings, recordingAudioUrl, type ConsultationRecording } from './recordingApi';

const when = (v: string) => new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const mins = (s?: string | null) => {
  const n = Number(s);
  return n > 0 ? `${Math.floor(n / 60)}:${String(Math.round(n % 60)).padStart(2, '0')}` : null;
};

export function RecordingsList({ visitId, version }: { visitId: number; version: number }) {
  const [rows, setRows] = useState<ConsultationRecording[] | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    listRecordings(visitId).then((r) => { if (live) setRows(r); }).catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [visitId, version]);

  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-3 border-t border-line pt-2">
      <button type="button" className="text-[12px] font-semibold text-teal" onClick={() => setOpen((o) => !o)}>
        {open ? '▾' : '▸'} Earlier recordings of this visit ({rows.length})
      </button>
      {open && (
        <ul className="mt-2 flex list-none flex-col gap-2 p-0">
          {rows.map((r) => <RecordingRow key={r.id} r={r} />)}
        </ul>
      )}
    </div>
  );
}

function RecordingRow({ r }: { r: ConsultationRecording }) {
  const [audio, setAudio] = useState<{ n: number; url: string } | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  useEffect(() => () => { if (audio) URL.revokeObjectURL(audio.url); }, [audio]);
  const parts = r.audio_purged_at ? 0 : (r.audio_file_count ?? 0);
  const load = async (n: number) => {
    setAudioError(null);
    try { setAudio({ n, url: await recordingAudioUrl(r.id, n) }); } catch (e) { setAudioError(e instanceof Error ? e.message : String(e)); }
  };
  return (
    <li className="rounded-[var(--radius)] border border-line bg-surface p-2.5 text-[12px]">
      <div className="mb-1 flex flex-wrap items-center gap-1.5 text-ink-muted">
        <span>{r.source === 'uploaded' ? `⬆ ${r.original_filename || 'Uploaded file'}` : '🎙 Recorded'}</span>
        <span>· {when(r.created_at)}</span>
        {mins(r.duration_seconds) && <span>· {mins(r.duration_seconds)}</span>}
        {r.recorded_by && <span>· {r.recorded_by}</span>}
        {r.status === 'failed' && <Badge variant="danger">Not transcribed</Badge>}
        {r.audio_purged_at && <Badge variant="neutral">Audio deleted (retention)</Badge>}
      </div>
      {r.status === 'failed'
        ? <div className="text-danger-ink">{r.error_message}</div>
        : <div className="whitespace-pre-wrap text-ink">{r.raw_text || '—'}</div>}
      {r.english_text && <div className="mt-1 whitespace-pre-wrap text-ink-muted">English: {r.english_text}</div>}
      {parts > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          <span className="text-ink-muted">▶ Audio{parts > 1 ? ' part' : ''}:</span>
          {Array.from({ length: parts }, (_, i) => i + 1).map((n) => (
            <Button key={n} size="sm" variant={audio?.n === n ? 'primary' : 'ghost'} onClick={() => load(n)}>
              {parts > 1 ? n : 'Play'}
            </Button>
          ))}
        </div>
      )}
      {audio && <audio key={audio.url} controls autoPlay src={audio.url} className="mt-1.5 h-8 w-full" />}
      {audioError && <div className="mt-1 text-danger-ink">{audioError}</div>}
    </li>
  );
}
